import * as React from 'react';
import * as Food from '../../domain/Food';
import * as Bar from './Bar';
import * as d3 from 'd3';
import { add, roundNumber } from '../../lib/number';

type EnergyProps = {
  food: Food.Food,
  grams: number,
  servings: number
};

type EnergyType = 'grams' | 'calories';
type EnergyState = {
  energyType: EnergyType
  displayResult: DisplayEnergyResult;
};

function filterNull<T>(arr: (T | null)[]): T[] {
  return (arr.filter((elem: T) => elem !== null) as T[]);
}

function flatMap<T, U>(array: T[], callbackfn: (value: T, index: number, array: T[]) => U[]): U[] {
  return (([] as U[]).concat(...array.map(callbackfn)) as U[]);
}

class Energy extends React.Component<EnergyProps, EnergyState> {
  constructor(props: EnergyProps) {
    super(props);
    const displayResult = displayEnergy({energyType: 'grams', ...props});
    this.state = {energyType: 'grams', displayResult };

    this.setEnergyType = this.setEnergyType.bind(this);
  }

  componentWillReceiveProps(nextProps: EnergyProps): void {
    const { energyType } = this.state;
    this.setState({
      displayResult: displayEnergy({energyType, ...nextProps})
    });
  }

  setEnergyType(energyType: EnergyType): void {
    const props: EnergyProps = this.props;
    this.setState({ energyType, displayResult: displayEnergy({ energyType, ...props }) });
  }

  componentDidMount(): void {
    const { displayResult } = this.state;
    if (displayResult) { displayResult.display(); }
  }

  componentDidUpdate(): void {
    const { displayResult } = this.state;
    if (displayResult) { displayResult.display(); }
  }

  render() {
    let barGraphRows: JSX.Element[] = [];
    const { energyType, displayResult } = this.state;
    const { grams, servings } = this.props;
    if (displayResult !== null) {
    const totalE = nodeValue(displayResult.dom_data);
    let already: {[key: string]: boolean} = {};
    const printData = (d: D): JSX.Element[] => {
      var depth: number = (d.depth || (d.depth = 0)) + 0.5;
      const tooltip = displayResult.tooltip(d);
      if (already[tooltip] === true) { return []; }
      already[tooltip] = true;
      const starting = displayNodeValue(this.props.food.Energy, energyType === 'calories', servings, grams, d);
      const barGraph = Bar.bar_graph(
          !d.parentNode ? 'Total Energy' :
            d.nodeName.indexOf('?') === -1 ? d.nodeName : displayResult.tooltip(d),
          starting,
          roundNumber(nodeValue(d) / totalE * 100), find_color(d), d.depth || 0);
      const depthNodes = d.childNodes.map(function(dc: D): D {
        dc.depth = depth;
        return dc;
      });
      return ([barGraph].concat(flatMap(depthNodes, printData)));
    };
    barGraphRows = printData(displayResult.dom_data);
    }

    return (
      <div id="energy">
        <p id="energy-type">
          <span id="energy-type-display">Display in</span>
          <br />
          <label>
            grams
            <input
              id="energy-grams"
              type="radio"
              value="grams"
              checked={energyType === 'grams'}
              onChange={() => { this.setEnergyType('grams'); }}
            />
          </label>
          <br />
          <label>
            calories
            <input
              id="energy-calories"
              type="radio"
              value="calories"
              checked={energyType === 'calories'}
              onChange={() => { this.setEnergyType('calories'); }}
            />
          </label>
        </p>

        <div id="energy-canvas" className="canvas" />
        <div style={{clear: 'both'}} />
        <table className="bar-graph">
          <tbody>
            {barGraphRows}
          </tbody>
        </table>
      </div>
    );
  }
}

export default Energy;

// TODO: make this the component responsible for rendering #energy

type D = { nodeName: string, nodeValue: number, parentNode: D | null, childNodes: D[], depth?: number };
type D3 = { name: string, value: number, children: D3[], parent?: D3 };
type AngleNode = {
  x: number
  dx: number
  depth: number
  y: number
  dy: number
};

function isD3Node(d: D | D3): d is D3 {
  return (d as D3).value !== undefined;
}

function getValues(obj: {[key: string]: number}): number[] {
  return Object.keys(obj).map(key => obj[key]);
}

const FAT_UNKNOWN = ' ?';
const MONO_UNKNOWN = ' ? ';
const POLY_UNKNOWN = ' ?  ';
const CARB_UNKNOWN = '? ';
const SUGAR_UNKNOWN = '?  ';
const SND_PROTEIN = ' ';
const SND_ALCOHOL = '  ';
const COLOR_LOOKUP = {
        energy: '#fff869',
        protein: '#bfc8de',
        alcohol: '#7b4173',
        fructose: '#7b4173',
        sucrose: '#de9ed6',
        glucose: '#a55194',
        maltose: '#a55194',
        lactose: '#ce6dbd',
        galactose: '#ce6dbd',
        carb: '#e7969c',
        sugar: '#d6616b',
        fiber: '#ad494a',
        starch: '#843c39',
        fat: '#ad8c4f',
        sat: '#e7cb94',
        mono: '#e7ba52',
        trans: '#dc6037',
        'trans ': '#dc6037',
        CLA: '#dc6037',
        poly: '#ffdf76',
        'n-3': '#f8f930',
        'n-6': '#dbda08'
      };
COLOR_LOOKUP[SND_PROTEIN] = COLOR_LOOKUP.protein;
COLOR_LOOKUP[SND_ALCOHOL] = COLOR_LOOKUP.alcohol;
COLOR_LOOKUP[CARB_UNKNOWN] = COLOR_LOOKUP.carb;
COLOR_LOOKUP[SUGAR_UNKNOWN] = COLOR_LOOKUP.sugar;
COLOR_LOOKUP[FAT_UNKNOWN] = COLOR_LOOKUP.fat;
COLOR_LOOKUP[POLY_UNKNOWN] = COLOR_LOOKUP.poly;
COLOR_LOOKUP[MONO_UNKNOWN] = COLOR_LOOKUP.mono;

type Hierarchy = {[key: string]: Hierarchy} | number;

interface EnergyHierarchy {
  alcohol: {'  ': number; };
  protein: { ' ': number; };
  fat: {
    sat: number;
    poly: {
      trans: number;
      CLA: number;
      'n-3': number;
      'n-6': number;
    }
    mono: {
      'trans ': number;
    }
  };
  carb: {
    fiber: number;
    starch: number;
    sugar: {
      sucrose: number;
      fructose: number;
      galactose: number;
      glucose: number;
      lactose: number;
      maltose: number;
    }
  };
  [key: string]: Hierarchy;
}

function makeNodes(e: EnergyHierarchy): D {
  function makeNode(depth: number, parent: D | null, name: string, children: Hierarchy): D {
    const node = {
        nodeName: name,
        nodeValue: 0,
        parentNode: parent,
        childNodes: ([] as D[]),
        depth: depth
    };
    if (typeof children === 'number') {
      node.nodeValue = children;
    } else if (typeof children === 'undefined' || typeof children === null) {
      node.nodeValue = 0;
    } else {
      const childNodes = Object.keys(children).map(key =>
        makeNode(depth + 1, node, key, children[key]) as D
      );
      node.childNodes = childNodes;
    }
    return node;
  }
  return makeNode(0, null, 'Energy', e as Hierarchy);
}

function pruneEmpty(dInput: D): D {
    function prune_empty_null(d: D): D | null {
        var v;
        if (v = d.nodeValue) {
          if (v === 0) {
            return null;
          } else {
            return d;
          }
        } else {
          if (d.childNodes.length === 1 && d.childNodes[0].nodeName.indexOf('?') !== -1) {
            d.nodeValue = d.childNodes[0].nodeValue;
            d.childNodes = [];
          } else {
            d.childNodes = filterNull(d.childNodes.map(prune_empty_null));
          }
          if (d.childNodes.length === 0) {
            return null;
          } else {
            return d;
          }
        }
    }

    const result = prune_empty_null(dInput);
    if (result === null) { throw new Error('empty prune'); }
    return result;
}

function find_color(d: D): string | null {
  return COLOR_LOOKUP[d.nodeName] || (!d.parentNode ? COLOR_LOOKUP.energy : null);
}
function find_color_d3(d: D3): string | null {
  return COLOR_LOOKUP[d.name] || (!d.parent ? COLOR_LOOKUP.energy : null);
}

function nodeValue(d: D | D3): number {
  if (isD3Node(d)) {
    return d.value;
  } else {
    return d.nodeValue || (d.nodeValue = d.childNodes.map(nodeValue).reduce(add));
  }
}

function to_d3_data(node: D): D3 {
  return {
    name: node.nodeName,
    value: node.nodeValue,
    children: node.childNodes.map(to_d3_data)
  };
}

// tslint:disable
function makeEnergyHierarchy(values: Food.EnergyValues): EnergyHierarchy {
  const data = {
      alcohol: {'  ': values['Alcohol, ethyl']},
      protein: {' ': values['Protein']},
      fat: {
        sat: values['Fatty acids, total saturated'],
        poly: {
          trans: values['Fatty acids, total trans-polyenoic'] - values['18:2 CLAs'],
          CLA: values['18:2 CLAs'],
          'n-3': values['n-3'],
          'n-6': values['n_6']
        },
        mono: {
          'trans ': values['Fatty acids, total trans-monounsaturated']
        }
      },
      carb: {
        fiber: values['Fiber, total dietary'],
        starch: values['Starch'],
        sugar: {
          sucrose: values['Sucrose'],
          fructose: values['Fructose'],
          galactose: values['Galactose'],
          glucose: values['Glucose (dextrose)'],
          lactose: values['Lactose'],
          maltose: values['Maltose']
        }
      }
    }

    const fat = data.fat;
    const poly: number = getValues(fat.poly).reduce(add)
    const mono: number = getValues(fat.mono).reduce(add)
    const unknown_fat = values["Total lipid (fat)"] - fat.sat - poly - mono;
    if (unknown_fat > 0) {
      fat[FAT_UNKNOWN] = unknown_fat;
    }
    const carb = data.carb
    if (carb) {
      const sugar = getValues(carb.sugar).reduce(add);
      const total_carb = values["Carbohydrate, by difference"] - sugar - carb.fiber - carb.starch;
      if (total_carb > 0) {
        carb[CARB_UNKNOWN] = total_carb;
      }
    }

    function check_unknown(parent_data: Object, key: string, unknown: string, total: number, ...children: string[]) {
      const object = parent_data[key]
      const child_sum = children.map((child) => object[child] || 0).reduce(add)
      if (child_sum > 0) {
        const unknown_value = total - child_sum;
        if (unknown_value > 0) { object[unknown] = unknown_value; }
      } else {
        parent_data[key] = total;
      }
    }
    check_unknown(data.carb, 'sugar', SUGAR_UNKNOWN, values["Sugars, total"], 'sucrose', 'fructose', 'galactose', 'glucose', 'lactose', 'maltose');
    check_unknown(data.fat, 'mono', MONO_UNKNOWN, values["Fatty acids, total monounsaturated"], 'trans ');
    check_unknown(data.fat, 'poly', POLY_UNKNOWN, values["Fatty acids, total polyunsaturated"], 'n-3', 'n-6', 'trans', 'CLA');

    return data;
}

function displayNodeValue(energyValue: number, inCalories: boolean, servings: number, grams: number, d: D | D3): string {
  const parent = isD3Node(d) ? d.parent : d.parentNode
  return displayValue(inCalories, inCalories && parent === null ? (servings * grams) / Food.GRAMS_GIVEN * energyValue : (servings * grams) / Food.GRAMS_GIVEN * nodeValue(d));
}

function displayValue(inCalories: boolean, value: number): string {
  return roundNumber(value, 1).toString() + (inCalories ? " Cal " : " g ");
};
// tslint:enable

let lastEnergyValues: Food.EnergyValues | null = null;
let lastEnergyType = 'grams';
// tslint:disable
type DisplayEnergyResult = null | { display: () => void, dom_data: D, tooltip: (d: D | D3) => string }
type DisplayEnergyArgs = EnergyProps & { energyType: EnergyType }
function displayEnergy(args: DisplayEnergyArgs): DisplayEnergyResult {
    let orig_values = args.food;
    let grams = args.grams;
    const servings = args.servings;
    const inCalories = 'calories' === (!args.energyType ? lastEnergyType : args.energyType);
    orig_values = orig_values || lastEnergyValues;
    lastEnergyValues = orig_values;
    const values = Object.assign({}, lastEnergyValues);
    if ((values['Energy'] || 0) < 1) {
      // if (orig_values.length > 0) {
        // I think this will automatically get reset due to changing state
        // $('#energy-canvas, #energy .bar-graph').empty();
      // }
      return null;
    }

    const data: EnergyHierarchy = makeEnergyHierarchy(values);
    let dom_data = pruneEmpty(makeNodes(data));
    function adjustValue(factor: number, d?: D): number {
      if (!d) return 0;

      if (d.childNodes && d.childNodes.length > 0) {
        d.nodeValue = d.childNodes.map((c: D) => adjustValue(factor, c)).reduce(add)
      } else {
        d.nodeValue = d.nodeValue * factor
      }
      return d.nodeValue
    };
    if (inCalories) {
      adjustValue(4, dom_data.childNodes.find((d: D) => d.nodeName === "protein"));
      adjustValue(9, dom_data.childNodes.find((d: D) => d.nodeName === "fat"));
      const carb = dom_data.childNodes.find((d:D) => d.nodeName === "carb");
      if (carb) {
        const alcohol = carb.childNodes.find((d:D) => d.nodeName === "alcohol");
        if (alcohol) { adjustValue(6.93, alcohol); }
        adjustValue(4, carb.childNodes.find((d: D) => d.nodeName !== "alcohol"));
      }
    }
    let d3_data = to_d3_data(dom_data);
    const TOOLTIP_LOOKUP = {
      sat: "saturated",
      mono: "monounsaturated",
      poly: "polyunsaturated",
      CLA: "conjugated linoleic acid (natural trans fat)",
      "n-3": "omega-3",
      "n-6": "omega-6",
      glucose: "glucose"
    };
    TOOLTIP_LOOKUP[FAT_UNKNOWN] = "unknown fat";
    TOOLTIP_LOOKUP[MONO_UNKNOWN] = "unknown mono";
    TOOLTIP_LOOKUP[POLY_UNKNOWN] = "unknown poly";
    TOOLTIP_LOOKUP[CARB_UNKNOWN] = "unknown carb";
    TOOLTIP_LOOKUP[SUGAR_UNKNOWN] = "unknown sugar";
    TOOLTIP_LOOKUP[SND_PROTEIN] = "protein";
    TOOLTIP_LOOKUP[SND_ALCOHOL] = "alcohol";
    function tooltip(d: D | D3): string {
      if (isD3Node(d)) {
        return TOOLTIP_LOOKUP[d.name] || (d.parent ? d.name : 'Total Energy');
      } else {
        return TOOLTIP_LOOKUP[d.nodeName] || (d.parentNode ? d.nodeName : 'Total Energy');
      }
    };

    const totalE = nodeValue(dom_data);
    dom_data.nodeName = displayValue(inCalories, totalE);
    function big_enough(d: {dx: number}): boolean {
      return d.dx > 0.1;
    };

    const w = 540;
    const h = 540;
    const r = Math.min(w, h) / 2;
    function display(){
    let ec = d3.select('#energy-canvas');
    ec.select('svg').remove();
    const vis = ec.append('svg').attr('width', w).attr('height', h).append("g").attr("transform", "translate(" + w / 2 + "," + h / 2 + ")");
    const partition = d3.layout.partition().size([2 * Math.PI, r + 40]);
    let arc = d3.svg.arc().startAngle(function(d: any) {
      return d.x;
    }).endAngle(function(d: any) {
      return d.x + d.dx;
    }).innerRadius(function(d: any) {
      return calcInnerRadius(d);
    }).outerRadius(function(d: any) {
      return calcOuterRadius(d);
    });
    function calcInnerRadius(d: AngleNode): number {
      if (d.depth) {
        return d.y - (d.dy / 2);
      } else {
        return 0;
      }
    };
    function calcOuterRadius(d: AngleNode): number {
      if (d.depth) {
        return d.y + d.dy - (d.dy / 2);
      } else {
        return d.y + d.dy + (d.dy / 2);
      }
    };
    const g = vis.data([d3_data] as any[]).selectAll("g").data(partition.nodes).enter().append("g");
    function getColor(d: D3, index: number): string {
      return find_color_d3(d as D3) || "";
    }
    g.append('path').attr("d", arc as any).style("fill", getColor as any).style('stroke', "#fff").attr('stroke-width', 0.5);
    /*
    hypotonuses = {};
    hypotonuse = function(d: AngleNode) {
      var _name;
      return hypotonuses[_name = [d.depth, d.y]] != null ? hypotonuses[_name] : hypotonuses[_name] = (r = calcInnerRadius(d), r + calcOuterRadius(d) - calcInnerRadius(d));
    };
    */
    const thetas : {[key: string]: number} = {};
    const theta = function(d: AngleNode) {
      const key = [d.depth, d.x, d.dx].toString();
      let value = thetas[key];
      if (!value) {
        value = !d.depth ? 0 : d.x + d.dx / 2 - Math.PI / 2;
        thetas[key] = value;
      }
      return value;
    };
    g.append("text").attr("transform", function(d: AngleNode) {
      var angle;
      angle = theta(d) / Math.PI * 180;
      if (angle > 90) {
        return "rotate(" + (angle - 180) + ") translate(-" + 2 * d.y + ",0)";
      } else {
        return "rotate(" + angle + ")";
      }
    }).attr("x", function(d: AngleNode) {
      if (!d.depth) {
        return 0;
      } else {
        return (calcInnerRadius(d) + calcOuterRadius(d)) / 2;
      }
    }).attr("y", function(d: AngleNode) {
      return d.x + 2;
    }).style('text-shadow', "0 0.1em 0.2em rgba(0,0,0,.6)").text(function(d: any) {
      return d.name;
    }).style('display', function(d: any): string {
      if (!big_enough(d)) { return 'none'; }
      return "";
    }).attr("text-anchor", "middle").attr("title", function(d: any): string {
      return displayNodeValue(values['Energy'], inCalories, servings, grams, d) + tooltip(d);
    }).append("title").text(function(d: any): string {
        return displayNodeValue(values['Energy'], inCalories, servings, grams, d) + tooltip(d);
    });
    /*
    $("#below-search").show();
    */
    }
    return { display, tooltip, dom_data };
}
// tslint:enable



// WEBPACK FOOTER //
// ./src/feature/graphs/Energy.tsx